<template>
  <modal-wrap
    :id="modalName"
    :name="modalName"
    :hide-footer="true"
    header-class="border-0 fix-error-header"
    body-class="integration-logs-modal"
    size="xl"
  >
    <template #modal-title>
      <h4 class="mb-0">Item Integration logs</h4>
    </template>
    <template #default>
      <easy-data-table
        ref="dataTable"
        v-model:server-options="serverOptions"
        table-class-name="vue3-easy-data-table table-shadow no-overflow"
        :server-items-length="serverItemsLength"
        :loading="loading"
        :headers="headers"
        :items="items"
        :prevent-context-menu-row="false"
        :fixed-header="false"
        no-hover
        alternating
        hide-footer
        @update:server-options="updateServerOptions"
      >
        <template #loading>
          <b-spinner class="mt-5" variant="success" />
        </template>
        <template #no-data>
          <div style="text-align: center">No data available</div>
        </template>
        <template #item="{ column, item }: { column: string; item: IntegrationLogItem }">
          <div v-if="column === 'logDate'">
            <RowDate :date="item.logDate" />
          </div>
          <div v-else-if="column === 'logDetails'">
            <span v-if="(item.logDetails?.length || 0) < 200">
              {{ item.logDetails }}
            </span>
            <div v-else>
              {{ formatLogMessage(item.logDetails).text }}
              <pre>{{ formatLogMessage(item.logDetails).json }}</pre>
            </div>
          </div>
        </template>
      </easy-data-table>

      <TablePagination
        v-model:server-options-ref="serverOptions"
        v-model:data-table-ref="dataTable"
        :server-items-length="serverItemsLength"
      />
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { IntegrationLogItem } from '@/types/Models/Assignment'
import { BSpinner } from 'bootstrap-vue-next'
import { defineComponent, watch, ref, computed, type ComputedRef, type Ref } from 'vue'
import { ITEM_INTEGRATION_MODAL } from '@/const/ModalTypes'
import { formatLogMessage } from '@/services/Helpers'
import { registerModals } from '@/services/Modal'
import { useItemStore } from '@/store/item'
import { useVueNextModalStore } from '@/store/vueNextModal'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import RowDate from '@/views/Components/Elements/Table/RowDate.vue'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'

export default defineComponent({
  name: 'ItemIntegrationsModal',
  components: {
    RowDate,
    TablePagination,
    EasyDataTable,
    BSpinner,
    ModalWrap,
  },
  setup() {
    const modalName = ITEM_INTEGRATION_MODAL
    const modalStore = useVueNextModalStore()
    const itemStore = useItemStore()
    const dataTable = ref()

    const serverOptions: Ref<ServerOptions> = ref({
      page: 1,
      rowsPerPage: 25,
    })
    const serverItemsLength: ComputedRef<number> = computed(() => itemStore.integrationLogsCount)
    const loading: ComputedRef<boolean> = computed(() => itemStore.integrationLogsLoading)
    const items: ComputedRef<IntegrationLogItem[]> = computed(() => itemStore.integrationLogs)
    const itemId: ComputedRef<string | null> = computed(() => modalStore.modalPropsByName(modalName)?.itemId || null)

    const headers: Header[] = [
      { text: 'Date', value: 'logDate' },
      { text: 'Details', value: 'logDetails' },
    ]

    const loadFromServer = () => {
      const id = itemId.value
      if (!id) {
        return
      }
      itemStore.fetchIntegrationLogs({ id, filter: serverOptions.value })
    }

    const updateServerOptions = (options: ServerOptions) => {
      serverOptions.value = options
      loadFromServer()
    }

    watch(itemId, loadFromServer, { immediate: true })

    registerModals(modalName)

    return {
      items,
      headers,
      loading,
      dataTable,
      modalName,
      serverOptions,
      serverItemsLength,
      updateServerOptions,
      formatLogMessage,
    }
  },
})
</script>
